/* Copyright 2016-2020 The MathWorks, Inc. */
define([
    'mw-messageservice/MessageService',
    'MW/toolstrip/constants/TypeConstants',
    'MW/uiframework/UIContainer',
    'MW/uiframework/uicontainer/ChildProperties',
    'MW/uiframework/uicontainer/DocumentTypeProperties',
    'dojo/_base/declare',
    'dojo/_base/lang',
    'dojo/dom-construct',
    'dojo/dom-class',
    'dojo/on',
    'dojo/dom',
    'dojo/dom-attr',
    'dojo/dom-style',
    'dojo/html',
    'dijit/layout/ContentPane',
    'BuildStatus/TabConfig',
    'BuildStatus/BuildStatusInfo',
    'dojo/i18n!BuildStatus/resources/RTW/nls/buildStatus',
    'dojo/domReady!'
], function (MessageService, TypeConstants, UIContainer, ChildProperties, DocumentTypeProperties,
    declare, lang, domConstruct, domClass, on, dom, domAttr, domStyle, domHTML,
    ContentPane, TabConfig, BuildStatusInfo, appL10n) {
    return declare(null, {
        _documentCount: 0,

        constructor: function (rootNode, paramObj) {
            this.domNode = rootNode;
            this.model = paramObj.model;

            var channelPrefix = '/BuildStatusUI/' + this.model;
            this.initializeChannel = channelPrefix + '/initialize';
            this.openBuildStatusChannel = channelPrefix + '/openBuildStatus';
            this.updateToolstripChannel = channelPrefix + '/updateToolstrip';
            this.reactToToolstripChannel = channelPrefix + '/reactToToolstrip';

            var container = new UIContainer(this.domNode.id, {
                title: appL10n.BuildStatus + ': ' + this.model,
                hasMnemonics: true,
                reserveDocumentSpace: true,
                tabGroupTags: [TabConfig.tag] // toolstrip config
            });
            this._loadActions(container); // toolstrip actions
            container.addTabGroupFromConfig(TabConfig, TabConfig.tag);
            this._addQABControls(container);
            this._registerDocumentTypes(container);
            this.container = container;
            this._currentDoc = { 'SIM': null, 'RTW': null };

            MessageService.start();

            this.container.startup();
            var that = this;
            on(window, 'beforeunload', function () {
                that.container.shutdown();
            });
        },

        start: function () {
            MessageService.subscribe(this.updateToolstripChannel,
                lang.hitch(this, '_handleUpdateToolstrip'),
                this);

            MessageService.subscribe(this.openBuildStatusChannel,
                lang.hitch(this, '_handleOpenBuildStatus'),
                this);

            MessageService.publish(this.initializeChannel, 'reset');
        },

        // -----------------------------------------------------------------------------
        // Toolstrip actions
        _loadActions: function (container) {
            container.loadActions([
                {
                    id: 'cancelBuild',
                    enabled: false,
                    text: appL10n.CancelBuildAct,
                    icon: 'end_24',
                    callback: lang.hitch(this, '_cancelBuild')
                },
                /*                 {
                    id: "openPA",
                    enabled: true,
                    text: appL10n.OpenPAAct,
                    icon: "export_24",
                    callback: lang.hitch(this, "_openPA")
                }, */
                {
                    id: 'help',
                    enabled: true,
                    text: appL10n.Help,
                    quickAccessIcon: 'help_16',
                    callback: lang.hitch(this, '_showHelp')
                }
            ]);
        },

        _cancelBuild: function (evt) {
            // once the button is pressed, disable it first.
            this.container.actionService.updateAction('cancelBuild', { enabled: false });
            MessageService.publish(this.reactToToolstripChannel, {
                action: 'cancelBuild'
            });
        },

        _openPA: function (evt) {
            MessageService.publish(this.reactToToolstripChannel, {
                action: 'openPA'
            });
        },

        _showHelp: function () {
            MessageService.publish(this.reactToToolstripChannel, {
                action: 'help'
            });
        },

        _addQABControls: function (container) {
            var tabGroupId = container.uiBuilder.tagToId('buildstatus.tabGroup');
            var QABGroupId = container.uiBuilder.get(tabGroupId, 'QAGroupId');

            container.uiBuilder.create({
                tag: 'buildstatus.qab.QABGroup.help',
                type: TypeConstants.QAB_PUSH_BUTTON,
                actionId: 'help',
                parentId: QABGroupId
            });
        },

        _registerDocumentTypes: function (container) {
            var properties = new DocumentTypeProperties({
                title: 'Documents'
            });
            container.registerDocumentType('buildstatus_document', properties);
        },

        _handleUpdateToolstrip: function (msg) {
            switch (msg.data.option) {
            case 'cancelBuildButton':
                this.container.actionService.updateAction('cancelBuild', { enabled: msg.data.isRunning });
                break;
            case 'openPAButton':
                this.container.actionService.updateAction('openPA', { enabled: !msg.data.isRunning });
                break;
            }
        },

        _handleOpenBuildStatus: function (msg) {
            switch (msg.data.option) {
            case 'openUI':
                // update initial status for toolstrip
                this.container.actionService.updateAction('cancelBuild', { enabled: msg.data.isRunning });
                this.container.actionService.updateAction('openPA', { enabled: !msg.data.isRunning });
                // remove existing document tabs
                this.container._documentContainer.clear();
                // open new tab
                this._handleOpenBuildStatusTab(msg);
                break;
            case 'openTab':
                this._handleOpenBuildStatusTab(msg);
                break;
            }
        },

        _handleOpenBuildStatusTab: function (msg) {
            // First check if the tab for current TargetType exists.
            var targetType = msg.data.targetType;
            var doc = this._checkDocument(targetType);
            if (doc === null) {
                // no existing tab
                this._createNewDocument(targetType, msg);
            } else {
                // if the tab for current TargetType exists, reuse the current tab.
                this._reuseDocument(targetType, msg, doc);
            }
        },

        _checkDocument: function (targetType) {
            var i, info;
            for (i = 0; i < this.container._documents.length; i++) {
                info = this.container._documents[i];
                if (info.properties.title === this._getDocTabTitle(targetType)) {
                    return info;
                }
            }
            return null;
        },

        _createNewDocument: function (targetType, initialMsg) {
            this._documentCount++;

            if (this._currentDoc[targetType]) {
                this._currentDoc[targetType].destroy();
            }
            var page = new BuildStatusInfo(this.model, targetType, initialMsg);

            var document = new ContentPane({
                container: this.container,
                style: 'height:100%; width:100%'
            });

            document.set('content', page.mainContainer);
            page.start();

            var properties = new ChildProperties({
                documentType: 'buildstatus_document',
                documentId: 'document_' + this._documentCount,
                title: this._getDocTabTitle(targetType),
                closable: false
            });
            this.container.addDocument(document, properties);

            this._currentDoc[targetType] = page;
        },

        _reuseDocument: function (targetType, initialMsg, document) {
            this._currentDoc[targetType].destroy();

            var page = new BuildStatusInfo(this.model, targetType, initialMsg);

            document.widget.set('content', page.mainContainer);
            page.start();

            this._currentDoc[targetType] = page;
        },

        _getDocTabTitle: function (targetType) {
            var docTabTitle;
            switch (targetType) {
            case 'SIM':
                docTabTitle = appL10n.SimTargets;
                break;
            case 'RTW':
                docTabTitle = appL10n.CodeGenTargets;
                break;
            default:
                docTabTitle = appL10n.NoExistingBuildStatus;
            }
            return docTabTitle;
        }

    });
});
